import { Fragment, useEffect } from 'react'

import { Box, Paper, useTheme } from '@mui/material'
import {
    Attribute,
    AttributeLabel,
    AttributeValue,
    Divider,
} from 'packages/eid-ui'
import { useResourceTypeContext } from 'resourceTypeContext'
import { useTranslation } from 'react-i18next'
import { getFullImageUrl } from 'utils'
import { isNilOrEmpty } from 'packages/core'
import DefaultGenericIcon from '../../../assets/images/Genric-1.svg'
import config from 'config'
import { AccessRequestPolicyDetails } from 'components/AccessRequestPolicyDetails'
import DomPurify from 'dompurify'
import ReactHtmlParser from 'react-html-parser'
import InfoBoxWithIcon from 'components/common/InfoBoxWithIcon'
const smallScreenWidth = 960

export const ApplicationOverView = (props: any) => {
    const theme = useTheme()
    const { data, policy } = props
    const [, dispatch]: any = useResourceTypeContext()
    const parsedHtml = ReactHtmlParser(DomPurify.sanitize(data?.instructions))
    useEffect(() => {
        if (data)
            dispatch({
                type: 'SET_PROP',
                payload: {
                    key: 'application',
                    value: data,
                },
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const { t } = useTranslation()

    const showAzureApplicationDetails =
        data &&
        data.isAzureApplication &&
        (data.azureApplicationFriendlyName ||
            data.azureApplicationDescription ||
            data.azureApplicationId ||
            data.servicePrincipleId ||
            data.applicationId)

    const azureApplicationDetails = data && (
        <Box>
            <AttributeLabel
                sx={{
                    color: theme?.palette?.common?.black,
                    fontSize: '14px',
                    textDecoration: 'underline',
                    margin: '1.6rem 0',
                    padding: '0',
                    minWidth: '250px',
                }}
            >
                {t('Common_AzureApplicationDetails')}
            </AttributeLabel>
            <Box display="flex" flexWrap="wrap">
                <Box paddingRight="32px">
                    {data.azureApplicationFriendlyName && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_FriendlyName')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationFriendlyName}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationDescription && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_Description')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationDescription}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureIntegrationType && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_Type_Of_Integration')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureIntegrationType}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationLine && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_Application_Line')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationLine}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureUpxId && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_Upx_ID')}
                            </AttributeLabel>
                            <AttributeValue>{data.azureUpxId}</AttributeValue>
                        </Attribute>
                    )}

                    {data.azureAuthenticationLevel && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_Authentication_Level')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureAuthenticationLevel}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.applicationId && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_AzureApplicationAppId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.applicationId}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationId && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_AzureApplicationObjectId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationId}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.servicePrincipleId && (
                        <Attribute orientation="horizontal">
                            <AttributeLabel>
                                {t('Common_ServicePrincipleId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.servicePrincipleId}
                            </AttributeValue>
                        </Attribute>
                    )}
                </Box>
            </Box>
        </Box>
    )

    const applicationDetails = (
        <Fragment>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Attribute orientation="horizontal">
                    <AttributeLabel>{t('Common_FriendlyName')}</AttributeLabel>
                    <AttributeValue>{data && data.friendlyName}</AttributeValue>
                </Attribute>

                <Attribute orientation="horizontal">
                    <AttributeLabel>{t('Common_TechnicalName')}</AttributeLabel>
                    <AttributeValue>{data && data.name}</AttributeValue>
                </Attribute>
                <Attribute orientation="horizontal">
                    <AttributeLabel>{t('Common_Description')}</AttributeLabel>
                    <AttributeValue>{data && data.description}</AttributeValue>
                </Attribute>
            </Box>
            <Box>
                <Paper
                    sx={{
                        margin: '0.8rem 1rem 0',
                        boxShadow: 'none !important',

                        '& img': {
                            maxHeight: '90px',
                            maxWidth: '90px',
                        },

                        [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                            '& img': {
                                width: '66px',
                            },
                        },
                    }}
                >
                    <img
                        alt={data?.friendlyName}
                        src={
                            isNilOrEmpty(data.applicationLogoImageUrl)
                                ? `${config.APP_SUBPATH}${DefaultGenericIcon}`
                                : getFullImageUrl(data?.applicationLogoImageUrl)
                        }
                    />
                </Paper>
            </Box>
        </Fragment>
    )

    const requestDetails = policy && (
        <>
            <Divider />
            <AccessRequestPolicyDetails
                policy={policy}
                orientation="horizontal"
            />
        </>
    )

    const renderContent = () => (
        <Fragment>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',

                    [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                        paddingBottom: theme.spacing(2),
                    },
                    [`@media (max-width:345px)`]: {
                        flexDirection: 'column-reverse',
                    },
                }}
            >
                {applicationDetails}
            </Box>
            {showAzureApplicationDetails && (
                <Box>{azureApplicationDetails}</Box>
            )}
            {requestDetails}
            {parsedHtml && parsedHtml.length > 0 && (
                <InfoBoxWithIcon
                    iconName={'InfoNew'}
                    message={parsedHtml}
                    rootStyles={{
                        width: '100%',
                        marginTop: '2.8rem',
                        marginBottom: '1.8rem',
                    }}
                />
            )}
        </Fragment>
    )

    return (
        <Paper
            sx={{
                position: 'relative',
                miHeight: '156px',
                boxShadow: 'none !important',
            }}
        >
            <Fragment>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',

                        [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                            paddingBottom: theme.spacing(2),
                        },
                        [`@media (max-width:345px)`]: {
                            flexDirection: 'column-reverse',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            padding: '0 3rem',
                            flexWrap: 'wrap',
                            flexDirection: 'column',
                            width: '100%',
                            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                                display: 'none',
                            },
                        }}
                    >
                        {renderContent()}
                    </Box>
                </Box>
            </Fragment>
        </Paper>
    )
}
